import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Plus, Search, Edit, CheckCircle, XCircle, Calendar, X, FileText, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConfirmModal from '@/components/ConfirmModal';
import { searchWithoutAccents } from '@/utils/helpers';

const Financeiros = ({ tipo }) => {
  const [financeiros, setFinanceiros] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFinanceiro, setEditingFinanceiro] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState({ isOpen: false, id: null });
  const [filtros, setFiltros] = useState({
    data_inicio: '',
    data_fim: '',
    status: ''
  });
  const [formData, setFormData] = useState({
    pessoa_id: '',
    tipo: tipo,
    categoria_id: '',
    titulo: '',
    descricao: '',
    documento: '',
    parcela_atual: 1,
    parcelas_total: 1,
    valor_original: '',
    desconto: 0,
    acrescimo: 0,
    emissao: new Date().toISOString().split('T')[0],
    vencimento: new Date().toISOString().split('T')[0],
    forma_pagamento: 'PIX',
    banco: '',
    status: 'em_aberto'
  });

  useEffect(() => {
    loadData();
  }, [tipo]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      params.append('tipo', tipo);
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);

      const [finResponse, pessoasResponse, catResponse] = await Promise.all([
        api.get(`/financeiros?${params.toString()}`),
        api.get('/pessoas'),
        api.get('/categorias')
      ]);
      setFinanceiros(finResponse.data);
      setPessoas(pessoasResponse.data.filter(p => p.ativo));
      setCategorias(catResponse.data.filter(c => c.tipo === tipo || c.tipo === 'ambos'));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (financeiro) => {
    setEditingFinanceiro(financeiro);
    setFormData({
      pessoa_id: financeiro.pessoa_id,
      tipo: tipo,
      categoria_id: financeiro.categoria_id,
      titulo: financeiro.titulo,
      descricao: financeiro.descricao || '',
      documento: financeiro.documento || '',
      parcela_atual: financeiro.parcela_atual,
      parcelas_total: financeiro.parcelas_total,
      valor_original: financeiro.valor_original,
      desconto: financeiro.desconto,
      acrescimo: financeiro.acrescimo,
      emissao: financeiro.emissao,
      vencimento: financeiro.vencimento,
      forma_pagamento: financeiro.forma_pagamento,
      banco: financeiro.banco || '',
      status: financeiro.status
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        valor_original: parseFloat(formData.valor_original),
        desconto: parseFloat(formData.desconto) || 0,
        acrescimo: parseFloat(formData.acrescimo) || 0
      };

      if (editingFinanceiro) {
        await api.put(`/financeiros/${editingFinanceiro.id}`, dataToSubmit);
        toast.success('Lançamento atualizado!');
      } else {
        if (dataToSubmit.parcelas_total > 1) {
          await api.post('/financeiros/parcelado', dataToSubmit);
          toast.success(`${dataToSubmit.parcelas_total} parcelas criadas!`);
        } else {
          await api.post('/financeiros', dataToSubmit);
          toast.success('Lançamento criado!');
        }
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleQuitar = async (id) => {
    try {
      await api.post(`/financeiros/${id}/quitar`);
      toast.success('Lançamento quitado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao quitar');
    }
  };

  const handleCancelar = async (id) => {
    try {
      await api.delete(`/financeiros/${id}`);
      toast.success('Lançamento cancelado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao cancelar');
    }
  };

  const handleRecibo = async (id) => {
    const toastId = toast.loading('Gerando recibo...');
    try {
      const { data: blob } = await api.get(`/financeiros/${id}/recibo`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recibo_BoraCar_${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success('Recibo gerado!', { id: toastId });
    } catch (error) {
      const msg = error.response?.status === 404 ? 'Recibo não encontrado' : 'Erro ao gerar recibo';
      toast.error(msg, { id: toastId });
    }
  };

  const resetForm = () => {
    setEditingFinanceiro(null);
    setFormData({
      pessoa_id: '',
      tipo: tipo,
      categoria_id: '',
      titulo: '',
      descricao: '',
      documento: '',
      parcela_atual: 1,
      parcelas_total: 1,
      valor_original: '',
      desconto: 0,
      acrescimo: 0,
      emissao: new Date().toISOString().split('T')[0],
      vencimento: new Date().toISOString().split('T')[0],
      forma_pagamento: 'PIX',
      banco: '',
      status: 'em_aberto'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const filteredFinanceiros = financeiros.filter(f =>
    !searchTerm ||
    searchWithoutAccents(f.titulo, searchTerm) ||
    (f.documento && f.documento.includes(searchTerm))
  );

  return (
    <div className="space-y-6 animate-slide-up" data-testid="financeiros-page">
      <ConfirmModal
        isOpen={confirmCancel.isOpen}
        onClose={() => setConfirmCancel({ isOpen: false, id: null })}
        onConfirm={() => handleCancelar(confirmCancel.id)}
        title="Cancelar Lançamento"
        message="Deseja realmente cancelar este lançamento? Esta ação não pode ser revertida."
        confirmText="Sim, Cancelar"
        cancelText="Não"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Contas a {tipo === 'pagar' ? 'Pagar' : 'Receber'}
          </h1>
          <p className="text-slate-600">Gerencie suas {tipo === 'pagar' ? 'despesas' : 'receitas'}</p>
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            variant="outline"
            className="border-slate-300"
          >
            <Filter className="w-4 h-4 mr-2" />
            {mostrarFiltros ? 'Ocultar' : 'Filtros'}
          </Button>
          <Button 
            data-testid="add-financeiro-button" 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Lançamento
          </Button>
        </div>
      </div>

      {/* Filtros Expandíveis */}
      {mostrarFiltros && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Data Início</Label>
              <Input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Data Fim</Label>
              <Input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select
                value={filtros.status}
                onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                className="input-field"
              >
                <option value="">Todos</option>
                <option value="em_aberto">Em Aberto</option>
                <option value="pago">Pago</option>
                <option value="vencido">Vencido</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={loadData} className="w-full bg-gradient-to-r from-red-600 to-orange-600">
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Formulário de lançamento... (continua igual) */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{editingFinanceiro ? 'Editar Lançamento' : 'Novo Lançamento'}</h2>
              <button onClick={() => setDialogOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pessoa (Cliente, Fornecedor ou Motorista)</Label>
                  <select
                    data-testid="financeiro-pessoa-select"
                    value={formData.pessoa_id}
                    onChange={(e) => setFormData({ ...formData, pessoa_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Selecione...</option>
                    {pessoas.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nome_razao}
                        {p.cliente && ' [Cliente]'}
                        {p.fornecedor && ' [Fornecedor]'}
                        {p.motorista && ' [Motorista]'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <select
                    data-testid="financeiro-categoria-select"
                    value={formData.categoria_id}
                    onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Selecione...</option>
                    {categorias.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label>Título</Label>
                <Input
                  data-testid="financeiro-titulo-input"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Valor Original</Label>
                  <Input
                    data-testid="financeiro-valor-input"
                    type="number"
                    step="0.01"
                    value={formData.valor_original}
                    onChange={(e) => setFormData({ ...formData, valor_original: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Desconto</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.desconto}
                    onChange={(e) => setFormData({ ...formData, desconto: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Acréscimo</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.acrescimo}
                    onChange={(e) => setFormData({ ...formData, acrescimo: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Parcelas (1 a 60)</Label>
                  <Input
                    data-testid="financeiro-parcelas-input"
                    type="number"
                    min="1"
                    max="60"
                    value={formData.parcelas_total}
                    onChange={(e) => setFormData({ ...formData, parcelas_total: parseInt(e.target.value) || 1 })}
                  />
                  {formData.parcelas_total > 1 && (
                    <p className="text-xs text-slate-600 mt-1">
                      Valor por parcela: R$ {(parseFloat(formData.valor_original || 0) / formData.parcelas_total).toFixed(2).replace('.', ',')}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Banco</Label>
                  <select
                    data-testid="financeiro-banco-select"
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Selecione...</option>
                    <option value="BB BoraMais">BB BoraMais</option>
                    <option value="BB BoraCar">BB BoraCar</option>
                    <option value="SICOOB">SICOOB</option>
                    <option value="Caixa Local">Caixa Local</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Forma de Pagamento</Label>
                  <select
                    value={formData.forma_pagamento}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="Dinheiro">Dinheiro</option>
                    <option value="Cartão de Crédito">Cartão de Crédito</option>
                    <option value="Cartão de Débito">Cartão de Débito</option>
                    <option value="PIX">PIX</option>
                    <option value="Boleto">Boleto</option>
                    <option value="Transferência">Transferência</option>
                  </select>
                </div>
                <div>
                  <Label>Documento/Nota</Label>
                  <Input
                    value={formData.documento}
                    onChange={(e) => setFormData({ ...formData, documento: e.target.value })}
                  />
                </div>
              </div>

              {editingFinanceiro && (
                <div>
                  <Label>Status (editável)</Label>
                  <select
                    data-testid="financeiro-status-select"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input-field"
                  >
                    <option value="em_aberto">Em Aberto</option>
                    <option value="pago">Pago</option>
                    <option value="vencido">Vencido</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Você pode alterar o status manualmente se necessário
                  </p>
                </div>
              )}

              {editingFinanceiro && formData.status === 'pago' && (
                <div>
                  <Label>Data de Pagamento</Label>
                  <Input
                    type="date"
                    value={formData.pagamento ? formData.pagamento.split('T')[0] : new Date().toISOString().split('T')[0]}
                    onChange={(e) => setFormData({ ...formData, pagamento: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Selecione a data em que o pagamento foi realizado
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data de Emissão</Label>
                  <Input
                    type="date"
                    value={formData.emissao}
                    onChange={(e) => setFormData({ ...formData, emissao: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Data de Vencimento</Label>
                  <Input
                    data-testid="financeiro-vencimento-input"
                    type="date"
                    value={formData.vencimento}
                    onChange={(e) => setFormData({ ...formData, vencimento: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="save-financeiro-button" type="submit" className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700">
                  Salvar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            data-testid="search-financeiros-input"
            placeholder="Buscar por título ou documento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Título</th>
                  <th>Vencimento</th>
                  <th>Data Pagamento</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredFinanceiros.map((fin) => (
                  <tr key={fin.id} data-testid={`financeiro-row-${fin.id}`}>
                    <td className="font-semibold">{fin.titulo}</td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{formatDate(fin.vencimento)}</span>
                      </div>
                    </td>
                    <td>
                      {fin.status === 'pago' && fin.pagamento ? (
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-green-500" />
                          <span className="text-green-700 font-semibold">{formatDate(fin.pagamento)}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="font-bold">{formatCurrency(fin.valor_liquido)}</td>
                    <td>
                      <span className={`status-badge status-${fin.status}`}>
                        {fin.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          data-testid={`edit-financeiro-${fin.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(fin)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {fin.status === 'pago' && (
                          <Button
                            data-testid={`recibo-financeiro-${fin.id}`}
                            size="sm"
                            variant="outline"
                            className="text-purple-600 hover:bg-purple-50"
                            onClick={() => handleRecibo(fin.id)}
                            title="Baixar Recibo"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                        {fin.status !== 'pago' && fin.status !== 'cancelado' && (
                          <Button
                            data-testid={`quitar-financeiro-${fin.id}`}
                            size="sm"
                            variant="outline"
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => handleQuitar(fin.id)}
                            title="Quitar"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {fin.status !== 'cancelado' && (
                          <Button
                            data-testid={`cancel-financeiro-${fin.id}`}
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => setConfirmCancel({ isOpen: true, id: fin.id })}
                            title="Cancelar"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Financeiros;